import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, ShieldCheck, Leaf } from "lucide-react";
import { api, fmtEUR } from "../lib/api";
import { primaryImg } from "../lib/img";
import { Badge } from "../components/ui/badge";
import EditorialMarquee from "../components/Marquee";
import { StarRatingDisplay } from "../components/StarRating";
import SEO, { organizationJsonLd } from "../components/SEO";
import { useSeoContext } from "../context/SeoContext";

function ProductCard({ p, testIdPrefix }) {
  return (
    <Link key={p.id} to={`/shop/${p.id}`} data-testid={`${testIdPrefix}-${p.id}`} className="product-card group bg-white border border-[#E5EAE5] rounded-2xl overflow-hidden">
      <div className="aspect-square overflow-hidden bg-[#F2F5F0]">
        <img src={primaryImg(p)} alt={p.name} className="zoom-img w-full h-full object-cover" />
      </div>
      <div className="p-5">
        <div className="flex gap-2 mb-2 flex-wrap">
          {p.is_new && <Badge className="bg-[#1A2E1A] hover:bg-[#1A2E1A] text-white rounded-full">Nouveauté</Badge>}
          {p.is_limited && <Badge className="bg-[#FACC15] text-[#1A2E1A] hover:bg-[#FACC15] rounded-full">Édition limitée</Badge>}
          {p.is_promo && <Badge className="bg-[#7AC143] hover:bg-[#7AC143] rounded-full">Promo</Badge>}
        </div>
        <h3 className="font-bold text-lg leading-tight">{p.name}</h3>
        {(p.rating_count || 0) > 0 && (
          <div
            className="mt-1 flex items-center gap-1.5 text-xs text-[#4B634B]"
            data-testid={`home-card-rating-${p.id}`}
          >
            <StarRatingDisplay value={p.rating_avg} size={13} />
            <span className="font-semibold text-[#1A2E1A]">{Number(p.rating_avg).toFixed(1)}</span>
            <span>· {p.rating_count} avis</span>
          </div>
        )}
        <div className="mt-3 flex items-baseline gap-2">
          {p.is_promo && p.promo_price ? (
            <>
              <span className="text-[#7AC143] font-black text-xl">{fmtEUR(p.promo_price)}</span>
              <span className="line-through text-sm text-[#4B634B]">{fmtEUR(p.price)}</span>
            </>
          ) : (
            <span className="text-[#1A2E1A] font-black text-xl">{fmtEUR(p.price)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

function ProductSection({ eyebrow, title, items, testId, testIdPrefix }) {
  if (!items || items.length === 0) return null;
  return (
    <section data-testid={testId} className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-20">
      <div className="flex items-end justify-between mb-12">
        <div>
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#7AC143] mb-3">{eyebrow}</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#1A2E1A]">{title}</h2>
        </div>
        <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#1A2E1A] hover:text-[#7AC143]">
          Tout voir <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((p) => (
          <ProductCard key={p.id} p={p} testIdPrefix={testIdPrefix} />
        ))}
      </div>
    </section>
  );
}

const DEFAULT_WEALTH_CTA = {
  image_url: "https://images.unsplash.com/photo-1758519288355-fe5b6fcc9f39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxsYXB0b3AlMjBjb2ZmZWUlMjBtZWV0aW5nJTIwc3VjY2VzcyUyMGJ1c2luZXNzfGVufDB8fHx8MTc3ODA0NzU3OHww&ixlib=rb-4.1.0&q=85",
  eyebrow: "Healthy Guides",
  title: "Prenez soin de votre bien-être",
  cta_label: "Lire les guides",
};
const DEFAULT_KITCHEN_CTA = {
  image_url: "https://images.unsplash.com/photo-1760445529436-25510c869789?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxoZWFsdGh5JTIwY29va2luZyUyMGZyZXNoJTIwc2FsYWQlMjBncmVlbiUyMGluZ3JlZGllbnRzfGVufDB8fHx8MTc3ODA0NzU3OHww&ixlib=rb-4.1.0&q=85",
  eyebrow: "Kitchen Corner",
  title: "Recettes saines & vibrantes",
  cta_label: "Voir les recettes",
};

export default function Home() {
  const [sections, setSections] = useState({ featured: [], bestsellers: [], new: [] });
  const [hero, setHero] = useState({
    image_url: "https://images.unsplash.com/photo-1777809088220-5ff1207fd376?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxncmVlbiUyMGFic3RyYWN0JTIwYmFja2dyb3VuZCUyMGJsdXJ8ZW58MHx8fHwxNzc4MDQ3NTgzfDA&ixlib=rb-4.1.0&q=85",
    eyebrow: "Édition limitée",
    title: "Berry Boost",
    description: "Énergie naturelle et antioxydants — quantité limitée.",
  });
  const [wealthCta, setWealthCta] = useState(DEFAULT_WEALTH_CTA);
  const [kitchenCta, setKitchenCta] = useState(DEFAULT_KITCHEN_CTA);
  const { global: seoGlobal } = useSeoContext();
  useEffect(() => {
    api.get("/products/homepage").then(r => setSections(r.data)).catch(() => {});
    api.get("/site/home-hero").then(r => setHero(r.data)).catch(() => {});
    api.get("/site/cta/home_wealth").then(r => setWealthCta(r.data)).catch(() => {});
    api.get("/site/cta/home_kitchen").then(r => setKitchenCta(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        pageSlug="home"
        path="/"
        jsonLd={[organizationJsonLd({ global: seoGlobal })].filter(Boolean)}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#F2F5F0] via-[#F9FAF6] to-white" />
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full bg-[#7AC143]/15 blur-3xl -z-10" />
        <div className="absolute -bottom-32 -left-20 w-[400px] h-[400px] rounded-full bg-[#FACC15]/10 blur-3xl -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-32 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 bg-white border border-[#E5EAE5] rounded-full px-4 py-1.5 text-xs font-bold tracking-[0.2em] uppercase text-[#7AC143] mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Distributeur indépendant
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#1A2E1A] leading-[1.05]">
              Votre <span className="hero-eyebrow">bien-être</span><br />commence ici.
            </h1>
            <p className="mt-6 text-lg text-[#4B634B] max-w-lg leading-relaxed">
              Découvrez la sélection Herbalife — nutrition essentielle, énergie vibrante et bien-être au quotidien.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/shop" data-testid="hero-shop-cta" className="inline-flex items-center gap-2 bg-[#7AC143] hover:bg-[#67BC2A] text-white rounded-full px-7 py-3.5 font-semibold transition-colors">
                Découvrir la boutique <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/healthy-guides" data-testid="hero-wealth-cta" className="inline-flex items-center gap-2 border border-[#1A2E1A] text-[#1A2E1A] hover:bg-[#1A2E1A] hover:text-white rounded-full px-7 py-3.5 font-semibold transition-colors">
                Healthy Guides
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-8 text-sm text-[#4B634B]">
              <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#7AC143]" /> Paiement sécurisé</div>
              <div className="flex items-center gap-2"><Leaf className="w-5 h-5 text-[#7AC143]" /> 100% naturel</div>
            </div>
          </div>

          <div className="relative h-[500px] lg:h-[600px] fade-up" data-testid="home-hero-highlight">
            <img
              src={hero.image_url}
              alt={hero.title}
              className="absolute inset-0 w-full h-full object-cover rounded-3xl"
            />
            <div className="absolute -bottom-6 -left-6 bg-white border border-[#E5EAE5] rounded-2xl p-5 shadow-lg max-w-[240px]">
              {hero.eyebrow && (
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#7AC143]">{hero.eyebrow}</div>
              )}
              <div className="font-black text-lg mt-1">{hero.title}</div>
              {hero.description && (
                <div className="text-sm text-[#4B634B] mt-1">{hero.description}</div>
              )}
            </div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* HOMEPAGE SECTIONS */}
      <ProductSection
        eyebrow="Sélection"
        title="Nos coups de cœur"
        items={sections.featured}
        testId="home-featured-section"
        testIdPrefix="featured-product"
      />
      <ProductSection
        eyebrow="Bestsellers"
        title="Les plus vendus"
        items={sections.bestsellers}
        testId="home-bestsellers-section"
        testIdPrefix="bestseller-product"
      />
      <ProductSection
        eyebrow="Nouveauté"
        title="Nouveautés"
        items={sections.new}
        testId="home-new-section"
        testIdPrefix="new-product"
      />

      {/* TWO BIG CTAs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 grid md:grid-cols-2 gap-6">
        <Link to="/healthy-guides" data-testid="home-wealth-card" className="relative overflow-hidden rounded-3xl bg-[#1A2E1A] text-white p-10 lg:p-14 min-h-[320px] flex flex-col justify-between group">
          {wealthCta.image_url && (
            <img src={wealthCta.image_url} className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700" alt="" />
          )}
          <div className="relative">
            {wealthCta.eyebrow && (
              <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#7AC143] mb-3">{wealthCta.eyebrow}</div>
            )}
            <h3 className="text-3xl lg:text-4xl font-black tracking-tight">{wealthCta.title}</h3>
          </div>
          {wealthCta.cta_label && (
            <div className="relative inline-flex items-center gap-2 font-semibold">
              {wealthCta.cta_label} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          )}
        </Link>
        <Link to="/kitchen-corner" data-testid="home-kitchen-card" className="relative overflow-hidden rounded-3xl bg-[#7AC143] text-white p-10 lg:p-14 min-h-[320px] flex flex-col justify-between group">
          {kitchenCta.image_url && (
            <img src={kitchenCta.image_url} className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 group-hover:scale-105 transition-all duration-700" alt="" />
          )}
          <div className="relative">
            {kitchenCta.eyebrow && (
              <div className="text-xs font-bold tracking-[0.3em] uppercase text-white/80 mb-3">{kitchenCta.eyebrow}</div>
            )}
            <h3 className="text-3xl lg:text-4xl font-black tracking-tight">{kitchenCta.title}</h3>
          </div>
          {kitchenCta.cta_label && (
            <div className="relative inline-flex items-center gap-2 font-semibold">
              {kitchenCta.cta_label} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          )}
        </Link>
      </section>
    </div>
  );
}
